import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { fetchCollection, fetchLocalizedCollection } from '../lib/cms';

export default function JobsPage() {
  const [jobs, setJobs] = useState([]);

  useEffect(() => {
    fetchLocalizedCollection('stillinger', 'da').then((items) => {
      if (items && items.length) {
        setJobs(items.sort((a, b) => (a.sortering || 0) - (b.sortering || 0)));
      }
    });
  }, []);
  return (
    <>
      <Helmet>
        <title>PMB job – ledige stillinger</title>
        <meta
          name="description"
          content="PMB job – ledige stillinger. Bliv en del af et stærkt hold hos POVL MØLLERS MASKINFABRIK A/S i Borup."
        />
        <meta property="og:title" content="PMB job – ledige stillinger" />
        <meta
          property="og:description"
          content="Her får du mulighed for at bruge dine kompetencer i en virksomhed med mere end 100 års erfaring – og hvor kvalitet og ordentlighed stadig betyder noget."
        />
      </Helmet>

      {/* Breadcrumb */}
      <div className="bg-stone-100 border-b border-stone-200 py-3 px-4 sm:px-6 lg:px-8 text-xs text-stone-600">
        <div className="max-w-7xl mx-auto flex items-center gap-2">
          <Link to="/" className="hover:text-[#006368] transition">
            Forside
          </Link>
          <i className="fa-solid fa-chevron-right text-[10px] text-stone-400" />
          <span className="text-stone-500">Om os</span>
          <i className="fa-solid fa-chevron-right text-[10px] text-stone-400" />
          <span className="font-semibold text-stone-900">Ledige stillinger</span>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-[#1a2024] text-white py-16 sm:py-24 border-b border-stone-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block px-3 py-1 rounded bg-[#006368] text-white text-xs font-bold uppercase tracking-wider mb-4">
              Karriere hos PMB
            </span>
            <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white leading-tight">
              Ledige stillinger
            </h1>
            <p className="mt-4 text-base sm:text-lg text-stone-300 leading-relaxed">
              Hos <strong>PMB</strong> arbejder vi med maskiner, der kræver både præcision og omtanke – og det samme gælder for de mennesker, der bygger og vedligeholder dem.
            </p>
            <p className="mt-2 text-sm sm:text-base text-stone-400 leading-relaxed">
              Her får du mulighed for at bruge dine kompetencer i en virksomhed med mere end 100 års erfaring – og hvor kvalitet og ordentlighed stadig betyder noget.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          {/* General Intro Box */}
          <div className="p-6 sm:p-8 rounded-lg bg-stone-50 border border-stone-200 text-stone-800 text-base leading-relaxed space-y-3">
            <p>
              Vi søger kolleger, der har lyst til at tage ansvar, arbejde fagligt stærkt og være en del af et team, der hjælper hinanden i hverdagen.
            </p>
            <p>
              Uanset om du er montør, tekniker, smed eller har erfaring fra produktion og service, kan der være en plads til dig hos os.
            </p>
          </div>

          {/* Job Cards */}
          {jobs.length > 0 ? (
            jobs.map((job) => {
              const email = job.kontakt_email || 'ps@pmborup.dk';
              return (
                <div
                  key={job.id}
                  className="bg-white rounded-lg border border-stone-200 overflow-hidden shadow-sm hover:border-[#006368] transition"
                >
                  <div className="bg-[#006368] text-white px-6 sm:px-8 py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div>
                      <span className="text-xs font-semibold uppercase tracking-wider text-white/80">
                        {job.terms?.['job-status']?.includes('arkiveret')
                          ? 'Besat stilling'
                          : 'Aktiv stilling'}
                      </span>
                      <h2 className="text-xl sm:text-2xl font-bold tracking-tight">
                        {job.titel}
                      </h2>
                    </div>
                    {job.opslagsdato && (
                      <span className="text-xs bg-white/15 px-3 py-1.5 rounded font-medium whitespace-nowrap self-start sm:self-auto">
                        Opslået: {job.opslagsdato}
                      </span>
                    )}
                  </div>

                  <div className="p-6 sm:p-8 space-y-6 text-stone-800 text-sm sm:text-base leading-relaxed">
                    {job.intro && (
                      <h3 className="text-lg font-bold text-[#006368] italic">
                        {job.intro}
                      </h3>
                    )}

                    {job.indhold ? (
                      <div
                        className="cms-content space-y-4"
                        dangerouslySetInnerHTML={{ __html: job.indhold }}
                      />
                    ) : null}

                    {/* Application details */}
                    <div className="pt-6 border-t border-stone-200 bg-stone-50 -mx-6 -mb-6 sm:-mx-8 sm:-mb-8 p-6 sm:p-8">
                      <h4 className="font-bold text-stone-900 text-base mb-2">
                        Har du lyst til at høre mere?
                      </h4>
                      <p className="text-sm text-stone-700 leading-relaxed mb-4">
                        Har du spørgsmål til stillingen, er du velkommen til at kontakte os på{' '}
                        <a
                          href={`mailto:${email}`}
                          className="text-[#006368] font-bold underline whitespace-nowrap"
                        >
                          {email}
                        </a>.
                      </p>
                      <p className="text-xs text-stone-600 mb-6">
                        Send gerne dit CV snarest muligt. Vi afholder samtaler løbende og ansætter, når vi har fundet den rette profil. Vi glæder os til at høre fra dig.
                      </p>

                      <div className="flex flex-wrap items-center gap-4">
                        <a
                          href={`mailto:${email}?subject=Ansøgning:%20${encodeURIComponent(job.titel)}`}
                          className="px-6 py-3 rounded bg-[#006368] hover:bg-[#004d51] text-white font-semibold text-sm transition shadow-sm flex items-center gap-2"
                        >
                          <i className="fa-solid fa-envelope" />
                          <span>Send ansøgning til {email}</span>
                        </a>
                        <a
                          href="tel:57526262"
                          className="px-6 py-3 rounded bg-white border border-stone-300 text-stone-800 font-semibold text-sm hover:bg-stone-100 transition whitespace-nowrap flex items-center gap-2"
                        >
                          <i className="fa-solid fa-phone text-[#006368]" />
                          <span className="whitespace-nowrap">Ring 57 52 62 62</span>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
          <div className="bg-white rounded-lg border border-stone-200 overflow-hidden shadow-sm hover:border-[#006368] transition">
            <div className="bg-[#006368] text-white px-6 sm:px-8 py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div>
                <span className="text-xs font-semibold uppercase tracking-wider text-white/80">
                  Aktiv stilling
                </span>
                <h2 className="text-xl sm:text-2xl font-bold tracking-tight">
                  Freelance montør til centralsmøring
                </h2>
              </div>
              <span className="text-xs bg-white/15 px-3 py-1.5 rounded font-medium whitespace-nowrap self-start sm:self-auto">
                Opslået: 11/05/2026
              </span>
            </div>

            <div className="p-6 sm:p-8 space-y-6 text-stone-800 text-sm sm:text-base leading-relaxed">
              <h3 className="text-lg font-bold text-[#006368] italic">
                Trives du med selvstændigt montagearbejde, serviceopgaver og skiftende arbejdsdage ude hos kunderne?
              </h3>

              <p>
                Hos PMB – Povl Møllers Maskinfabrik A/S tilbyder vi en alsidig hverdag, hvor du arbejder tæt sammen med kunder, teknikere og kollegaer i en rolle med stor frihed, ansvar og afveksling.
              </p>

              <p>
                Vi søger en erfaren freelance montør til montering og service af centralsmøring, hvor du får en selvstændig hverdag med montageopgaver ude hos kunderne. Du bliver en del af en virksomhed, hvor faglighed, fleksibilitet og langvarige relationer er en vigtig del af måden, vi arbejder på.
              </p>

              <p>
                Har du erfaring med montagearbejde, hydraulik, maskiner eller industrielt udstyr, og trives du med frihed under ansvar, så er det måske dig, vi leder efter.
              </p>

              <div className="pt-4 border-t border-stone-100">
                <h4 className="font-bold text-stone-900 text-base mb-3">
                  Din hverdag består af montage og service af centralsmøresystemer hos kunder i Jylland og på Fyn
                </h4>
                <p className="text-stone-600 mb-3 text-sm">
                  En stor del af din arbejdsdag foregår ude hos kunder inden for industri og entreprenørbranchen, hvor du arbejder selvstændigt med installation og service:
                </p>
                <ul className="list-disc pl-5 space-y-2 text-stone-700 text-sm">
                  <li>Du monterer og servicerer forskellige typer centralsmøresystemer</li>
                  <li>Du arbejder med løsninger fra blandt andet Vogel og SKF</li>
                  <li>Du planlægger og løser dine opgaver selvstændigt ude hos kunderne</li>
                  <li>Du fejlfinder og tilpasser installationer i samarbejde med kunden</li>
                  <li>Du arbejder kvalitetsbevidst og har en professionel dialog med kunderne</li>
                  <li>Du samarbejder med kollegaer og kunder omkring montage og installation af centralsmøring</li>
                  <li>Du bidrager til, at kunden får en professionel og god oplevelse med PMB</li>
                </ul>
              </div>

              <div className="pt-4 border-t border-stone-100">
                <h4 className="font-bold text-stone-900 text-base mb-3">
                  Du passer godt ind, hvis:
                </h4>
                <ul className="list-disc pl-5 space-y-2 text-stone-700 text-sm">
                  <li>Du har erfaring med montagearbejde, hydraulik eller maskiner</li>
                  <li>Du arbejder selvstændigt og trives med ansvar</li>
                  <li>Du har hænderne skruet rigtigt på og sans for kvalitet</li>
                  <li>Du kan strukturere din egen arbejdsdag og prioritere dine opgaver</li>
                  <li>Du trives med skiftende arbejdssteder og fleksible arbejdstider</li>
                  <li>Du er serviceminded og god til dialogen med kunderne</li>
                  <li>Erfaring fra industri, entreprenørbranchen eller lignende tekniske miljøer er en fordel</li>
                </ul>
                <p className="mt-3 text-sm text-stone-600">
                  Din baggrund kan eksempelvis være maskinarbejder, skibsmontør, entreprenørmekaniker, rørlægger eller lignende teknisk/håndværksmæssig erfaring.
                </p>
              </div>

              <div className="pt-4 border-t border-stone-100">
                <h4 className="font-bold text-stone-900 text-base mb-3">
                  Arbejdstid og arbejdsområde
                </h4>
                <p className="text-sm text-stone-700">
                  Stillingen er freelancebaseret, og arbejdet foregår hovedsageligt i Jylland og på Fyn, men enkelte opgaver på Sjælland kan forekomme.
                  Vi forestiller os, at du er bosiddende i Midtjylland eller Trekantområdet.
                </p>
                <div className="mt-3 bg-stone-50 p-4 rounded border border-stone-200">
                  <p className="font-semibold text-stone-900 text-sm mb-1">Du skal have:</p>
                  <ul className="list-disc pl-5 text-sm text-stone-700 space-y-1">
                    <li>Kørekort B</li>
                    <li>Egen servicebil og almindeligt håndværktøj</li>
                  </ul>
                  <p className="mt-2 text-xs text-stone-500">
                    PMB stiller specialværktøj til rådighed.
                  </p>
                </div>
                <p className="mt-3 text-xs text-stone-500 leading-relaxed">
                  Stillingen kræver fleksibilitet i forhold til arbejdstider, da montage hos nogle kunder først kan udføres uden for normal arbejdstid eller i weekender. Der kan derfor i perioder forekomme arbejdsuger med mere end 37 timer.
                </p>
              </div>

              {/* Application details */}
              <div className="pt-6 border-t border-stone-200 bg-stone-50 -mx-6 -mb-6 sm:-mx-8 sm:-mb-8 p-6 sm:p-8">
                <h4 className="font-bold text-stone-900 text-base mb-2">
                  Har du lyst til at høre mere?
                </h4>
                <p className="text-sm text-stone-700 leading-relaxed mb-4">
                  Har du spørgsmål til stillingen, er du velkommen til at kontakte <strong>Adm. direktør Palle Sivertsen</strong> på{' '}
                  <a href="mailto:ps@pmborup.dk" className="text-[#006368] font-bold underline whitespace-nowrap">
                    ps@pmborup.dk
                  </a>.
                </p>
                <p className="text-xs text-stone-600 mb-6">
                  Send gerne dit CV snarest muligt. Vi afholder samtaler løbende og ansætter, når vi har fundet den rette profil. Vi glæder os til at høre fra dig.
                </p>

                <div className="flex flex-wrap items-center gap-4">
                  <a
                    href="mailto:ps@pmborup.dk?subject=Ansøgning:%20Freelance%20montør%20til%20centralsmøring"
                    className="px-6 py-3 rounded bg-[#006368] hover:bg-[#004d51] text-white font-semibold text-sm transition shadow-sm flex items-center gap-2"
                  >
                    <i className="fa-solid fa-envelope" />
                    <span>Send ansøgning til ps@pmborup.dk</span>
                  </a>
                  <a
                    href="tel:57526262"
                    className="px-6 py-3 rounded bg-white border border-stone-300 text-stone-800 font-semibold text-sm hover:bg-stone-100 transition whitespace-nowrap flex items-center gap-2"
                  >
                    <i className="fa-solid fa-phone text-[#006368]" />
                    <span className="whitespace-nowrap">Ring 57 52 62 62</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
          )}
        </div>
      </section>
    </>
  );
}
